import WarningAmberTwoToneIcon from '@mui/icons-material/WarningAmberTwoTone';
import { Box, Chip, Grid, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { first } from 'lodash';
import { useMemo } from 'react';
import { useGetPublication } from 'src/hooks/api/publication/useGetPublication';
import {
  ICCAsset,
  IProduct,
  IProductVariation,
  IProductVariationFlavor,
  IProductVariationSize,
  PublicationTypes,
} from 'src/interfaces';
import { getCompareAndFilteredData, lang, useUserRole } from 'src/utils';
import { getProductNameFromAssetId } from 'src/utils/gcf-helper';

import {
  highlightKeyword,
  normalizeText,
} from '../highlightKeyword/HighlightKeyword';
import {
  ProductListModalContainerLabel,
  ProductListModalContainerLabelName,
} from '../ProductListModal.styled';

interface CustomProductLabelProps {
  product: IProduct;
  assets: ICCAsset[];
  inputValue: string[];
  productVariationTypes?: IProductVariationSize[];
  productVariationFlavors?: IProductVariationFlavor[];
}

export const CustomProductLabel: React.FC<CustomProductLabelProps> = ({
  product,
  assets,
  inputValue,
  productVariationTypes = [],
  productVariationFlavors = [],
}) => {
  const { data } = useGetPublication(PublicationTypes.PRODUCTS);
  const { isOwnerOperator } = useUserRole();

  const lastModifiedProduct = product?.modified_at;
  const lastPublished = data?.last_published_at;

  const isVariationModifiedAfterPublish = product?.productVariations?.some(
    (variation) =>
      variation.modified_at &&
      dayjs(variation.modified_at).isAfter(dayjs(lastPublished)),
  );

  const isProductModifiedAfterPublish =
    lastModifiedProduct &&
    dayjs(lastModifiedProduct).isAfter(dayjs(lastPublished));

  // Show warning if either the product or any variation is modified after publish
  const showWarning =
    isProductModifiedAfterPublish || isVariationModifiedAfterPublish;
  const [searchProductCode, searchProductName, searchProductOverrideNameVal] =
    inputValue;
  const productVariations = useMemo(
    () => product?.productVariations ?? [],
    [product],
  );

  const visibleVariations = useMemo(() => {
    // show all variations only when there is a warning AND the user is NOT owner/operator
    if (showWarning && !isOwnerOperator) return productVariations;

    // Build filteredVariations and determine if ALL variations were modified
    const filteredVariations: IProductVariation[] = [];
    let allVariationsModified = productVariations.length > 0;

    for (const variation of productVariations) {
      const isModified =
        variation.modified_at &&
        dayjs(variation.modified_at).isAfter(dayjs(lastPublished));

      if (!isModified) {
        // If any variation is NOT modified after publish , we keep it
        allVariationsModified = false;
        filteredVariations.push(variation);
      }
    }

    // If ALL variations were modified after publish -> hide all
    return allVariationsModified ? [] : filteredVariations;
  }, [showWarning, isOwnerOperator, productVariations, lastPublished]);

  const productNameBySearchValue = productVariations.find((variation) =>
    normalizeText(variation.name).includes(normalizeText(searchProductName)),
  )?.name;

  const productName =
    product?.name_override ||
    productNameBySearchValue ||
    getProductNameFromAssetId(assets, first(product.asset_id)) ||
    `Product #${product?.product_code}`;

  const getVariationLabel = (variation: IProductVariation) => {
    const sizeName = getCompareAndFilteredData(
      [variation],
      productVariationTypes,
      'product_variation_size_id',
    )?.name;
    const flavorName = getCompareAndFilteredData(
      [variation],
      productVariationFlavors,
      'product_variation_flavor_id',
    )?.name;

    return (
      <>
        {highlightKeyword(variation?.product_code, searchProductCode)}
        {` `}
        {`${sizeName} ${flavorName}`}
      </>
    );
  };

  return (
    <ProductListModalContainerLabel>
      <ProductListModalContainerLabelName>
        <Typography>
          {highlightKeyword(product?.product_code, searchProductCode)}
          <span> - </span>
          {highlightKeyword(
            productName,
            searchProductName || searchProductOverrideNameVal,
          )}
        </Typography>
        <Grid container gap={1}>
          {visibleVariations.map((variation, index) => (
            <Chip
              key={index}
              label={getVariationLabel(variation)}
              color="default"
              size="small"
              variant="outlined"
            />
          ))}
        </Grid>
      </ProductListModalContainerLabelName>

      <Box display="flex" alignItems="center" gap={1}>
        {showWarning && !isOwnerOperator && (
          <Tooltip title={lang.warningAmberTwoToneIconText}>
            <WarningAmberTwoToneIcon
              sx={{ color: 'warning.main', fontSize: 18 }}
              aria-label={lang.warningAmberTwoToneIconText}
            />
          </Tooltip>
        )}
        <Typography noWrap>
          {`${visibleVariations.length} ${
            visibleVariations.length > 1 ? `${lang.variant}s` : lang.variant
          }`}
        </Typography>
      </Box>
    </ProductListModalContainerLabel>
  );
};
